import { Box, Chip, TextLink, Typography } from '@drone-js/core';
import React from 'react';
import { connect } from 'react-redux';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/search/SortAndFilter/SelectedFilter/SelectedFilter.selectors';
import styled from 'styled-components';
import FilterTooltipWrapper, { getTruncatedText } from 'component/page/search/SortAndFilter/Filter/FilterTooltipWrapper';
import { FeatureName } from 'core/constant/FeatureName';
import Feature from 'component/common/Experiment/Feature';

// Below styled components are temporary solution, DO NOT use them in other places, we will remove them when we have a better solution.
const StyledChip = styled(Chip)`
    white-space: normal;
`;

const StyledTextLink = styled(TextLink)`
    text-align: right;
    overflow-wrap: anywhere;
`;

export interface SelectedFilterProps {
    label: string;
    onDismiss: () => void;
    elementName: string;
    isRatingType?: boolean;
    index: number;
    isSubcategoryFilterEnabled: boolean;
}

const SelectedFilterChip: React.FC<SelectedFilterProps> = React.memo((props) => {
    return (
        <>
            {props.isSubcategoryFilterEnabled ? (
                <FilterTooltipWrapper title={props.label}>
                    <Chip
                        data-element-name="activities-search-selected-filter-chip"
                        size="s"
                        label={getTruncatedText(props.label)}
                        dismissible={true}
                        dismissLabel={`dismiss-${props.elementName}`}
                        data-testid={props.elementName}
                        data-element-index={props.index + 1}
                        selection="selected"
                        onDismiss={props.onDismiss}
                        minWidth="content-fit"
                        disabled={true}
                        type={props.isRatingType ? 'rating-star' : 'default'}
                        me="8"
                        mb="8"
                    />
                </FilterTooltipWrapper>
            ) : (
                <StyledChip
                    data-element-name="activities-search-selected-filter-chip"
                    size="s"
                    label={props.label}
                    dismissible={true}
                    dismissLabel={`dismiss-${props.elementName}`}
                    data-testid={props.elementName}
                    data-element-index={props.index + 1}
                    selection="selected"
                    onDismiss={props.onDismiss}
                    minWidth="content-fit"
                    disabled={true}
                    type={props.isRatingType ? 'rating-star' : 'default'}
                    me="8"
                    mb="8"
                />
            )}
        </>
    );
});

SelectedFilterChip.displayName = 'SelectedFilterChip';

const SelectedFilter: React.FC<Props> = React.memo((props) => {
    if (!props.hasFilter) return null;

    return (
        <Box pb="24" px="16">
            <Box data-testid="activities-selected-filter" display="flex" flexDirection="row" justifyContent="space-between" pb="16">
                <Typography
                    data-element-name="activities-selected-filter-heading"
                    variant="heading-default"
                    role="heading"
                    aria-level={2}
                    aria-label={props.cms[264336]}
                    id="activities-selected-filter-heading"
                    me="4"
                >
                    {props.cms[264336]}
                </Typography>
                {/* eslint-disable-next-line @agoda/agoda-a11y/no-non-drone-interactable -- @agoda/agoda-a11y/no-non-drone-interactable[1ebb694a-b288-4dcf-9ddf-c265e6ab186c] */}
                <StyledTextLink
                    hierarchy="primary"
                    label={props.cms[252532]}
                    onClick={props.handleClearAllFilter}
                    data-element-name="activities-search-selected-filter-clear-all-button"
                    data-testid="clear-all-selected-filter-text-link"
                />
            </Box>
            <Box display="flex" flexWrap="wrap">
                {!props.isSubcategoryFilterEnabled &&
                    props.selectedCategoryFilter.map((item, index) => (
                        <SelectedFilterChip
                            key={item.label}
                            index={index}
                            label={item.label}
                            elementName={`selected-category-filter-${item.label}`}
                            onDismiss={props.handleClearCategoryFilter}
                            isSubcategoryFilterEnabled={props.isSubcategoryFilterEnabled}
                        />
                    ))}
                {props.isSubcategoryFilterEnabled &&
                    props.selectedCategoryWithSubcategoryFilter.map((item, index) => (
                        <SelectedFilterChip
                            key={item.label}
                            index={index}
                            label={item.label}
                            elementName={`selected-category-filter-${item.label}`}
                            onDismiss={() => props.handleClearCategoryWithSubcategoryFilter(item.value)}
                            isSubcategoryFilterEnabled={props.isSubcategoryFilterEnabled}
                        />
                    ))}
                {props.selectedSubcategoryFilter.map((item, index) => (
                    <SelectedFilterChip
                        key={item.label}
                        index={index}
                        label={item.label}
                        elementName={`selected-subcategory-filter-${item.label}`}
                        onDismiss={() => props.handleClearSubcategoryFilter(item.id)}
                        isSubcategoryFilterEnabled={props.isSubcategoryFilterEnabled}
                    />
                ))}
                {props.isShowPriceFilter && (
                    <SelectedFilterChip
                        index={0}
                        label={props.selectedPriceFilterText}
                        elementName="selected-price-filter"
                        onDismiss={props.handleClearPriceFilter}
                        isSubcategoryFilterEnabled={props.isSubcategoryFilterEnabled}
                    />
                )}
                {props.selectedStarRatingFilter.map((item, index) => (
                    <SelectedFilterChip
                        isRatingType={true}
                        key={item.min}
                        label={item.min !== 0 ? item.min.toString() : props.cms[150030]}
                        elementName={`selected-star-rating-filter-${item.min}`}
                        index={index}
                        onDismiss={() => props.handleClearStarRatingFilter(item)}
                        isSubcategoryFilterEnabled={props.isSubcategoryFilterEnabled}
                    />
                ))}
                <Feature.On name={FeatureName.NewFilters}>
                    {props.selectedBookingFeatureFilter.map((item, index) => (
                        <SelectedFilterChip
                            key={item.label}
                            label={item.label}
                            elementName={`selected-booking-feature-filter-${item.label}`}
                            index={index}
                            onDismiss={() => props.handleClearValueFilter(item.values, item.code)}
                            isSubcategoryFilterEnabled={props.isSubcategoryFilterEnabled}
                        />
                    ))}
                </Feature.On>
                {props.selectedDurationFilter.map((item, index) => (
                    <SelectedFilterChip
                        key={item.label}
                        label={item.label}
                        elementName={`selected-duration-filter-${item.label}`}
                        index={index}
                        onDismiss={() => props.handleClearValueFilter(item.values, item.code)}
                        isSubcategoryFilterEnabled={props.isSubcategoryFilterEnabled}
                    />
                ))}
            </Box>
        </Box>
    );
});

SelectedFilter.displayName = 'SelectedFilter';
export default connect(mapStateToProps, mapDispatchToProps)(SelectedFilter);
